"use client";

import { useLiveBroadcasts } from "@/contexts/live-broadcast-context";
import { LiveIndicator } from "@/components/ui/live-indicator";
import { cn } from "@/lib/utils";
import { ExternalLink, Eye, Radio } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatViewers, timeAgo } from "@/lib/twitch-live-format";
import {
  getLiveProviderBrand,
  resolveLiveHeaderCopy,
} from "@/lib/live-broadcast-branding";

export function LiveBroadcastHeader() {
  const { isLive, streams, clickCta } = useLiveBroadcasts();

  if (!isLive || streams.length === 0) {
    return null;
  }

  const headerCopy = resolveLiveHeaderCopy(streams);

  const button = (
    <button
      type="button"
      className={cn(
        "relative flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] sm:w-9 sm:h-9 sm:min-w-[36px] sm:min-h-[36px] rounded-md transition-colors",
        "text-muted-foreground hover:text-foreground hover:bg-white/5",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40",
      )}
      aria-label={headerCopy.ariaLabel}
    >
      <Radio className="size-4" />
      <span className="absolute top-1.5 right-1.5 flex">
        <span className="animate-ping absolute inline-flex size-2 rounded-full bg-primary/60" />
        <span className="relative inline-flex size-2 rounded-full bg-primary" />
      </span>
    </button>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{button}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {headerCopy.title}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side="right"
          align="end"
          sideOffset={8}
          className="w-72 p-0"
        >
          <div className="p-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <LiveIndicator size="sm" />
              <h3 className="text-sm font-semibold text-foreground">
                {headerCopy.title}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Watch live Upside betting strategy
            </p>
          </div>

          <div>
            {streams.map((stream) => {
              const brand = getLiveProviderBrand(stream.provider);
              return (
                <button
                  type="button"
                  key={stream.id}
                  onClick={() => clickCta(stream.watchUrl, stream)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px]",
                    "hover:bg-white/[0.03] transition-colors",
                    "text-left border-b border-white/5 last:border-b-0",
                  )}
                  data-provider={stream.provider}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {stream.channelName}
                      </p>
                      <span
                        className={cn(
                          "shrink-0 px-1.5 py-px rounded text-[9px] font-semibold uppercase tracking-wider",
                          brand.chipClassName,
                        )}
                        aria-label={`${brand.platformName} stream`}
                      >
                        {brand.platformName}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      {stream.viewerCount !== null ? (
                        <span className="inline-flex items-center gap-0.5">
                          <Eye className="size-3" />
                          <span className="data-num">
                            {formatViewers(stream.viewerCount)}
                          </span>
                        </span>
                      ) : null}
                      <span>{timeAgo(stream.startedAt)}</span>
                    </p>
                  </div>
                  <ExternalLink className="size-3.5 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>

          <div className="p-2 border-t border-white/5">
            <p className="text-[10px] text-muted-foreground/50 text-center">
              {headerCopy.footer}
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}
